import { http } from '@services/http/client';
import type { AuthProvider, AuthTokens, AuthUser } from '@app-types/index';

import { ENDPOINTS } from './endpoints';

// ---------------------------------------------------------------------------
// Wire-format types (snake_case, as the backend serializers emit them).
// Kept private to this module — the rest of the app sees camelCase AuthUser.
// ---------------------------------------------------------------------------
interface RawUser {
  id: string;
  email: string;
  name: string;
  auth_provider: AuthProvider;
  default_forward_to: string;
  is_email_verified: boolean;
  is_onboarded: boolean;
  is_suspended: boolean;
  has_password: boolean;
  connected_providers: AuthProvider[];
  onboarded_at: string | null;
  date_joined: string;
}

interface LoginResponse {
  access: string;
  refresh: string;
  user: RawUser;
}

interface RefreshResponse {
  access: string;
  refresh?: string;
}

export interface AuthSession {
  tokens: AuthTokens;
  user: AuthUser;
}

const mapUser = (raw: RawUser): AuthUser => ({
  id: raw.id,
  email: raw.email,
  name: raw.name,
  authProvider: raw.auth_provider,
  defaultForwardTo: raw.default_forward_to,
  isEmailVerified: raw.is_email_verified,
  isOnboarded: raw.is_onboarded,
  isSuspended: raw.is_suspended,
  hasPassword: raw.has_password,
  // Defensive: very old backend responses may not include this field yet.
  // Treat missing as "no extra providers" and fall back to has_password.
  connectedProviders: raw.connected_providers ?? (raw.has_password ? ['email'] : []),
  onboardedAt: raw.onboarded_at,
  dateJoined: raw.date_joined,
});

const mapSession = (raw: LoginResponse): AuthSession => ({
  tokens: { access: raw.access, refresh: raw.refresh },
  user: mapUser(raw.user),
});

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const authApi = {
  login: async (email: string, password: string): Promise<AuthSession> => {
    const data = await http.post<unknown, LoginResponse>(ENDPOINTS.auth.login, {
      email,
      password,
    });
    return mapSession(data);
  },

  register: async (payload: {
    email: string;
    password1: string;
    password2: string;
    name: string;
  }): Promise<void> => {
    // Mandatory verification — backend returns 204 with no body (or detail).
    await http.post(ENDPOINTS.auth.register, payload);
  },

  logout: async (refresh: string | null): Promise<void> => {
    // dj-rest-auth blacklists the refresh token server-side when sent.
    await http.post(ENDPOINTS.auth.logout, refresh ? { refresh } : {});
  },

  refresh: async (refresh: string): Promise<RefreshResponse> => {
    return http.post<unknown, RefreshResponse>(ENDPOINTS.auth.refresh, { refresh });
  },

  googleLogin: async (idToken: string): Promise<AuthSession> => {
    const data = await http.post<unknown, LoginResponse>(ENDPOINTS.auth.google, {
      // dj-rest-auth's SocialLoginSerializer requires `access_token` or `code`
      // for input validation. When the provider is Google AND `id_token` is
      // also present, the adapter prefers the id_token (verifying it against
      // Google's JWKS) and ignores the access_token — so we re-use the same
      // GSI credential for both fields. This is the documented pattern for
      // Google Identity Services + dj-rest-auth.
      id_token: idToken,
      access_token: idToken,
    });
    return mapSession(data);
  },

  verifyEmail: async (key: string): Promise<void> => {
    await http.post(ENDPOINTS.auth.verifyEmail, { key });
  },

  resendVerification: async (email: string): Promise<void> => {
    await http.post(ENDPOINTS.auth.resendVerification, { email });
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    await http.post(ENDPOINTS.auth.passwordReset, { email });
  },

  confirmPasswordReset: async (payload: {
    uid: string;
    token: string;
    newPassword1: string;
    newPassword2: string;
  }): Promise<void> => {
    await http.post(ENDPOINTS.auth.passwordResetConfirm, {
      uid: payload.uid,
      token: payload.token,
      new_password1: payload.newPassword1,
      new_password2: payload.newPassword2,
    });
  },

  changePassword: async (payload: {
    oldPassword: string;
    newPassword1: string;
    newPassword2: string;
  }): Promise<void> => {
    await http.post(ENDPOINTS.auth.passwordChange, {
      old_password: payload.oldPassword,
      new_password1: payload.newPassword1,
      new_password2: payload.newPassword2,
    });
  },

  getMe: async (): Promise<AuthUser> => {
    const raw = await http.get<unknown, RawUser>(ENDPOINTS.me.detail);
    return mapUser(raw);
  },

  patchMe: async (payload: { name?: string; defaultForwardTo?: string }): Promise<AuthUser> => {
    const body: Record<string, string> = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.defaultForwardTo !== undefined) {
      body.default_forward_to = payload.defaultForwardTo;
    }
    const raw = await http.patch<unknown, RawUser>(ENDPOINTS.me.detail, body);
    return mapUser(raw);
  },

  completeOnboarding: async (payload: {
    name?: string;
    defaultForwardTo?: string;
  }): Promise<AuthUser> => {
    const body: Record<string, string> = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.defaultForwardTo !== undefined) {
      body.default_forward_to = payload.defaultForwardTo;
    }
    const raw = await http.post<unknown, RawUser>(ENDPOINTS.me.completeOnboarding, body);
    return mapUser(raw);
  },
};
